import type { League } from '@h2h/shared';

const SHORT_DATE: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short' };

/** Race window (epoch ms) from the league's persisted cycling meta, if known. */
export function cyclingRaceWindow(league: League): { startMs: number; endMs: number } | null {
  const c = league.cycling;
  if (c?.startsAt == null) return null;
  return { startMs: c.startsAt, endMs: c.endsAt ?? c.startsAt };
}

/**
 * A race is finished once its final day has passed. Derived from the persisted
 * race window so the lobby can hide finished races even when no stage round was
 * ever synced. Races with an unknown window are treated as NOT finished (shown),
 * so a missing date never hides a race we can't reason about.
 */
export function isCyclingRaceFinished(league: League, now = Date.now()): boolean {
  const end = league.cycling?.endsAt ?? league.cycling?.startsAt ?? null;
  return end != null && now > end;
}

export function formatCyclingRaceDates(startMs: number, endMs: number): string {
  if (startMs === endMs) {
    return new Date(startMs).toLocaleDateString('en-GB', SHORT_DATE);
  }
  const start = new Date(startMs);
  const end = new Date(endMs);
  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    const left = start.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' });
    const right = end.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    return `${left} – ${right}`;
  }
  const left = start.toLocaleDateString('en-GB', SHORT_DATE);
  const right = end.toLocaleDateString('en-GB', SHORT_DATE);
  return `${left} – ${right}`;
}

/** "Sat, 4 Jul – Sun, 26 Jul" for the race, or null when dates are unknown. */
export function cyclingRaceDateLabel(league: League): string | null {
  const window = cyclingRaceWindow(league);
  return window ? formatCyclingRaceDates(window.startMs, window.endMs) : null;
}

/** "Grand Tour · Sat, 4 Jul – Sun, 26 Jul" — group plus the race window when known. */
export function cyclingLeagueMetaLine(league: League): string {
  const label = league.cycling?.group ?? 'Cycling';
  const dates = cyclingRaceDateLabel(league);
  return dates ? `${label} · ${dates}` : label;
}

/** Sort key: race start (epoch ms); unknown windows sort last. */
export function cyclingRaceSortKey(league: League): number {
  return league.cycling?.startsAt ?? Number.MAX_SAFE_INTEGER;
}

/** Upcoming/active cycling races (finished ones removed), ordered by start date. */
export function upcomingCyclingLeagues(leagues: League[], now = Date.now()): League[] {
  return leagues
    .filter((l) => !isCyclingRaceFinished(l, now))
    .sort((a, b) => cyclingRaceSortKey(a) - cyclingRaceSortKey(b) || a.name.localeCompare(b.name));
}
